import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { AbstractControl, ControlContainer, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DataRowState, EmailShare } from "@tylertech/shared-module";
import { Command, LabelCommand } from "@tylertech/commands-module";
import
{
    LgdFormControl,
    LgdFormControlFactory,
    LgdFormControlTextConfig,
    MaskedSocialSecurityValidator,
    PhoneValidator,
    setEnabled,
} from "@tylertech/lgd-reactive-forms";
import { SecurityService } from "@tylertech/core-module";
import { IShellService } from "@tylertech/incode-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import
{
    COContact,
    DocumentSearchCriteria,
    EntityAlert,
    EntityNote,
    ExecutionResult,
    GetNoteObjectViewModel,
    IdValuePair,
    MailingAddressViewModel,
    UdfContext,
    UserDefinedData,
} from "@tylertech/tyler-api-types";
import { EntityAlertCode } from "../../interfaces/entityAlertCode";
import { ContactService } from "../../services";
import { DocumentService } from "@tylertech/attachments-module";
enum ContactManagerSteps
{
    Main,
    Alert,
    Note,
    Attachment,
    Udf,
    Address,
}
const enum ContactType
{
    Individual = "Individual",
    Company = "Company",
}
interface MaskedFieldData
{
    identifier: string;
    canEdit: boolean;
    canView: boolean;
    unmaskedValue: string;
    maskedValue: string;
    isMasked: boolean;
    iconName: string;
    tooltipMessage: string;
    control: AbstractControl;
}
const enum maskIcon
{
    Visibility = "visibility",
    VisibilityOff = "visibility_off",
}
const enum maskTooltip
{
    View = "View ",
    Hide = "Hide ",
}
@Component({
    selector: "contact-manager",
    templateUrl: "./contactManager.tmpl.html",
    standalone: false
})
export class ContactManagerComponent implements OnInit, OnDestroy
{
    private readonly $securityService = inject(SecurityService);
    private readonly $shellService = inject(IShellService);
    private readonly $documentApiService = inject(DocumentService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $contactService = inject(ContactService);
    private readonly controlContainer = inject(ControlContainer, { optional: true });

    private readonly destructor$ = new Subject<void>();
    private readonly contactTypesSubject = new BehaviorSubject<ReadonlyArray<ContactType>>([
        ContactType.Individual,
        ContactType.Company,
    ]);

    private readonly contactSetsSubject = new BehaviorSubject<ReadonlyArray<IdValuePair>>([]);
    private readonly titlesSubject = new BehaviorSubject<ReadonlyArray<string>>([]);
    private readonly suffixesSubject = new BehaviorSubject<ReadonlyArray<string>>([]);
    private readonly mailingAddressesSubject = new BehaviorSubject<ReadonlyArray<MailingAddressViewModel>>([]);
    readonly menuCommands: ReadonlyArray<Command>;
    readonly contactForm: FormGroup;
    /** @deprecated */ private _childViewShown: boolean;
    /** @deprecated */ private currentStep: ContactManagerSteps = ContactManagerSteps.Main;
    /**
     * TODO - I think this is just to allow some code to run once on initialization, but not in response to user-interaction?
     * if true, this should be implemented differently...
     * @deprecated
     */
    private openingContact: boolean = true;
    @Input()
    contact: COContact;

    //TODO: Refactor TypeArgument to exported interface
    @Output()
    readonly viewChange = new EventEmitter<{
        isChildView: boolean;
    }>();

    /** @deprecated */ attachmentCount: number;
    /** @deprecated */ attachmentTitle: string;
    private canAdd: boolean;
    private canEdit: boolean;
    /**
     * Used in Template; handed to <alert-list> element
     * @deprecated
     */
    alertCodes: EntityAlertCode[];
    // /** @deprecated */ contactSets: IdValuePair[];
    entityType: string = "Contacts.Contact";
    private hasLaserfiche: boolean;
    hasTcm: boolean;
    isProcessing: boolean;
    isShowContactDetails: boolean;
    readonly udfSettings: UdfContext = {
        Id: undefined,
        Type: "Contact.Contact",
    };

    readonly notesSettings: GetNoteObjectViewModel = {
        Id: undefined,
        Type: "Contact",
    };

    constructor()
    {
        this.contactForm = new FormGroup({
            contactType: LgdFormControlFactory.radioGroup<string>(undefined, true),
            name: LgdFormControlFactory.text(undefined, true, {
                maxLength: () => (this.isCompany ? 100 : 165),
            }),
            showContactDetails: LgdFormControlFactory.checkbox(),
            contactSet: LgdFormControlFactory.select<IdValuePair>(undefined, true, {
                displayValueFn: x => x.Value,
            }),
            title: LgdFormControlFactory.select<string>(undefined, false),
            firstName: LgdFormControlFactory.text(
                undefined,
                () => this.isShowContactDetails && this.contact.IsIndividual,
                {
                    maxLength: 50,
                }
            ),
            middleName: LgdFormControlFactory.text(undefined, false, {
                maxLength: 50,
            }),
            lastName: LgdFormControlFactory.text(
                undefined,
                () => this.isShowContactDetails && this.contact.IsIndividual,
                {
                    maxLength: 50,
                }
            ),
            suffix: LgdFormControlFactory.select<string>(undefined, false),
            mailingAddress: LgdFormControlFactory.select<MailingAddressViewModel>(undefined, true, {
                displayValueFn: x => x.FullMailingAddress,
            }),
            phoneNumber: LgdFormControlFactory.text(undefined, false, {}, [new PhoneValidator()]),
            email: LgdFormControlFactory.text(undefined, false, {
                maxLength: 100,
            }),
            phoneNumber2: LgdFormControlFactory.text(undefined, false, {}, [new PhoneValidator()]),
            employerIdentificationNumber: LgdFormControlFactory.text(undefined, false, {
                maxLength: 20,
            }),
            phoneNumber3: LgdFormControlFactory.text(undefined, false, {}, [new PhoneValidator()]),
            socialSecurityNumber: LgdFormControlFactory.text(
                undefined,
                false,
                {
                    maxLength: 11,
                },
                [new MaskedSocialSecurityValidator()]
            ),
            driversLicenseNumber: LgdFormControlFactory.text(undefined, false, {
                maxLength: 30,
            }),
            dateOfBirth: LgdFormControlFactory.date(undefined, false, {}),
            isNoCheckPayment: LgdFormControlFactory.checkbox(),
            isNoCardPayment: LgdFormControlFactory.checkbox(),
            driversLicenseState: LgdFormControlFactory.text(undefined, false, {
                maxLength: 50,
            }),
        });
        this.menuCommands = [
            new LabelCommand({
                label: () => `${this.attachmentTitle} (${this.attachmentCount})`,
                action: () => this.openAttachmentList(),
                getEnabled: () => this.contact.RowState !== DataRowState.Added,
            }),
            new LabelCommand({
                label: "Notes",
                action: () => this.openNoteList(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "User Defined Data",
                action: () => this.openUdfList(),
                getEnabled: () => true,
            }),
            new LabelCommand({
                label: "Audit Log",
                action: () => this.openAuditLog(),
                getEnabled: () => this.$shellService.isEmbedded(),
                tooltip: undefined,
                getVisible: () => this.$shellService.isEmbedded(),
            }),
            new LabelCommand({
                label: "Misc. Receipts",
                action: () => this.openMiscReceiptSearch(),
                getEnabled: () => this.$shellService.isEmbedded(),
                tooltip: undefined,
                getVisible: () => this.$shellService.isEmbedded(),
            }),
        ];
    }

    private get showMiscReceiptSearchUrl(): boolean
    {
        return this.contact && !this.isContactNew() && this.contact.ShowMiscReceiptSearchLink;
    }

    private get parentForm(): FormGroup | undefined
    {
        return (this.controlContainer?.control as FormGroup) || undefined;
    }

    private get contactTypeControl(): AbstractControl<ContactType>
    {
        return this.contactForm?.get("contactType");
    }

    private get nameControl(): LgdFormControl<LgdFormControlTextConfig, string>
    {
        return this.contactForm.get("name") as LgdFormControl<LgdFormControlTextConfig, string>;
    }

    private get showContactDetailsControl(): AbstractControl<boolean>
    {
        return this.contactForm.get("showContactDetails");
    }

    private get contactSetControl(): AbstractControl<IdValuePair>
    {
        return this.contactForm.get("contactSet");
    }

    private get titleControl(): AbstractControl<string>
    {
        return this.contactForm.get("title");
    }

    private get firstNameControl(): AbstractControl<string>
    {
        return this.contactForm.get("firstName");
    }

    private get middleNameControl(): AbstractControl<string>
    {
        return this.contactForm.get("middleName");
    }

    private get lastNameControl(): AbstractControl<string>
    {
        return this.contactForm.get("lastName");
    }

    private get suffixControl(): AbstractControl<string>
    {
        return this.contactForm.get("suffix");
    }

    private get mailingAddressControl(): AbstractControl<MailingAddressViewModel>
    {
        return this.contactForm.get("mailingAddress");
    }

    private get phoneNumberControl(): AbstractControl<string>
    {
        return this.contactForm.get("phoneNumber");
    }

    private get emailControl(): AbstractControl<string>
    {
        return this.contactForm.get("email");
    }

    private get phoneNumber2Control(): AbstractControl<string>
    {
        return this.contactForm.get("phoneNumber2");
    }

    private get employerIdentificationNumberControl(): AbstractControl<string>
    {
        return this.contactForm.get("employerIdentificationNumber");
    }

    private get phoneNumber3Control(): AbstractControl<string>
    {
        return this.contactForm.get("phoneNumber3");
    }

    private get socialSecurityNumberControl(): AbstractControl<string>
    {
        return this.contactForm.get("socialSecurityNumber");
    }

    private get driversLicenseNumberControl(): AbstractControl<string>
    {
        return this.contactForm.get("driversLicenseNumber");
    }

    private get dateOfBirthControl(): AbstractControl<Date>
    {
        return this.contactForm.get("dateOfBirth");
    }

    private get isNoCheckPaymentControl(): AbstractControl<boolean>
    {
        return this.contactForm.get("isNoCheckPayment");
    }

    private get isNoCardPaymentControl(): AbstractControl<boolean>
    {
        return this.contactForm.get("isNoCardPayment");
    }

    private get driversLicenseStateControl(): AbstractControl<string>
    {
        return this.contactForm.get("driversLicenseState");
    }

    get contactTypesObservable(): Observable<ReadonlyArray<ContactType>>
    {
        return this.contactTypesSubject.asObservable();
    }

    get contactSetsObservable(): Observable<ReadonlyArray<IdValuePair>>
    {
        return this.contactSetsSubject.asObservable();
    }

    get titlesObservable(): Observable<ReadonlyArray<string>>
    {
        return this.titlesSubject.asObservable();
    }

    get suffixesObservable(): Observable<ReadonlyArray<string>>
    {
        return this.suffixesSubject.asObservable();
    }

    get mailingAddressesObservable(): Observable<ReadonlyArray<MailingAddressViewModel>>
    {
        return this.mailingAddressesSubject.asObservable();
    }

    get showMainForm(): boolean
    {
        return this.currentStep === ContactManagerSteps.Main;
    }

    get showAlerts(): boolean
    {
        return this.currentStep === ContactManagerSteps.Alert;
    }

    get showNotes(): boolean
    {
        return this.currentStep === ContactManagerSteps.Note;
    }

    get showAttachments(): boolean
    {
        return this.currentStep === ContactManagerSteps.Attachment;
    }

    get showUserDefinedFields(): boolean
    {
        return this.currentStep === ContactManagerSteps.Udf;
    }

    get showMailingAddresses(): boolean
    {
        return this.currentStep === ContactManagerSteps.Address;
    }

    get isViewOnly(): boolean
    {
        return !this.canEdit && !this.canAdd;
    }

    get isCompany(): boolean
    {
        return this.contactTypeControl?.value === ContactType.Company;
    }

    /** @deprecated */
    get alertCount(): number
    {
        if (!this.contact?.Alerts) return 0;
        return this.contact.Alerts.filter(a => !a.IsDeleted).length;
    }

    private socialSecurityNumberData: MaskedFieldData;
    private driversLicenseNumberData: MaskedFieldData;
    private employerIdentificationNumberData: MaskedFieldData;
    ngOnDestroy(): void
    {
        this.parentForm?.removeControl("contactForm");
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.parentForm?.addControl("contactForm", this.contactForm);
        this.canEdit = this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactEdit");
        this.canAdd = this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactAdd");
        const canView = this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactView");
        if (this.contact.RowState === DataRowState.Added && !this.canAdd)
        {
            throw new Error("User cannot add contacts, yet somehow managed it...");
        }
        if (!canView)
        {
            throw new Error("User cannot view this....");
        }
        this.setMaskedFieldData();
        this.udfSettings.Id = this.contact.ContactId;
        this.notesSettings.Id = this.contact.ContactId;
        // TODO - why are we doing this? shouldn't the backend have sent us this already set?
        for (const mailingAddress of this.contact.MailingAddresses)
        {
            if (mailingAddress.IsPrimary)
            {
                this.contact.OldMailingAddressId = mailingAddress.Id;
            }
        }
        Promise.all([
            this.mailingAddressesSubject.next(
                this.contact.MailingAddresses.filter(x => x.RowState !== DataRowState.Deleted)
            ),
            this.$contactService.getContactSets().then((result: IdValuePair[]) =>
            {
                this.contactSetsSubject.next(result);
                if (result.length === 1)
                {
                    // this.contact.ContactSetId = this.contactSets[0].Id;
                    this.contactSetControl.setValue(result[0]);
                    this.contactSetControl.updateValueAndValidity();
                }
            }),
            this.$contactService.getAlertCodes().then((result: EntityAlertCode[]) =>
            {
                this.alertCodes = result;
            }),
            this.$documentApiService
                .GetContentManagementType()
                .then((contentManagementType: string) =>
                {
                    // this.setupAttachmentsByType(result, self);
                    if (contentManagementType === "Tyler Content Management")
                    {
                        this.setAttachmentLabels("Contacts.Contact-TCM", "TCM", true, false);
                    }
                    else if (contentManagementType === "Attachments")
                    {
                        this.setAttachmentLabels("Contacts.Contact", "Attachments", false, false);
                    }
                    else
                    {
                        this.setAttachmentLabels("", "", false, true);
                    }
                })
                .then(() => this.updateDocumentCount()),
            this.$contactService.getTitles().then((result: string[]) =>
            {
                this.titlesSubject.next(result);
            }),
            this.$contactService.getSuffixes().then((result: string[]) =>
            {
                this.suffixesSubject.next(result);
            }),
        ]);
        this.nameControl.focus();
        this.openingContact = false;
        this.contactTypeControl.setValue(this.contact.IsIndividual ? ContactType.Individual : ContactType.Company);
        subscribeToValueChanges(this.contactTypeControl, (newValue: ContactType) =>
        {
            this.contact.IsIndividual = newValue === ContactType.Individual;
            if (newValue === ContactType.Company)
            {
                this.clearNameFields();
                this.isShowContactDetails = false;
            }
            else
            {
                // We dont want to run the name parser when opening a contact. It can change how a name was saved.
                // e.g. FName: Dreamer, LName: Mc Gee  --is parsed to-->  FName: Dreamer, MName: Mc, LName: Gee
                // TODO - we should be setting the initial value before setting this subscription up, is this still necessary?
                if (!this.openingContact)
                {
                    this.updateNameDetails();
                }
            }
        });
        setEnabled(this.nameControl, !this.isViewOnly).setValue(this.contact.Name);
        subscribeToValueChanges(this.nameControl, (newValue: string) =>
        {
            this.contact.Name = newValue;
            this.updateNameDetails();
        });
        subscribeToValueChanges(this.showContactDetailsControl, (newValue: boolean) =>
        {
            this.isShowContactDetails = newValue;
            // TODO - enable/disable name part controls?
            // this.showContactDetails();
            if (
                !this.isShowContactDetails &&
                (this.firstNameControl.invalid || this.middleNameControl.invalid || this.lastNameControl.invalid)
            )
            {
                this.isShowContactDetails = true;
            }
        });
        setEnabled(this.contactSetControl, !this.contact.ContactSetId && this.isAddMode() && !this.isViewOnly).setValue(
            this.contact.ContactSetId
        );
        subscribeToValueChanges(this.contactSetControl, (newValue: IdValuePair) =>
        {
            this.contact.ContactSetId = newValue.Id;
        });
        setEnabled(this.titleControl, !this.isViewOnly).setValue(this.contact.Title);
        subscribeToValueChanges(this.titleControl, (newValue: string) =>
        {
            this.contact.Title = newValue;
            this.updateFullName();
        });
        setEnabled(this.firstNameControl, !this.isViewOnly).setValue(this.contact.FirstName);
        subscribeToValueChanges(this.firstNameControl, (newValue: string) =>
        {
            this.contact.FirstName = newValue;
            this.updateFullName();
        });
        setEnabled(this.middleNameControl, !this.isViewOnly).setValue(this.contact.MiddleName);
        subscribeToValueChanges(this.middleNameControl, (newValue: string) =>
        {
            this.contact.MiddleName = newValue;
            this.updateFullName();
        });
        setEnabled(this.lastNameControl, !this.isViewOnly).setValue(this.contact.LastName);
        subscribeToValueChanges(this.lastNameControl, (newValue: string) =>
        {
            this.contact.LastName = newValue;
            this.updateFullName();
        });
        setEnabled(this.suffixControl, !this.isViewOnly).setValue(this.contact.Suffix);
        subscribeToValueChanges(this.suffixControl, (newValue: string) =>
        {
            this.contact.Suffix = newValue;
            this.updateFullName();
        });
        setEnabled(this.mailingAddressControl, !this.isViewOnly).setValue(
            this.contact.MailingAddresses?.find(x => x.IsPrimary)
        );
        subscribeToValueChanges(this.mailingAddressControl, (newValue: MailingAddressViewModel) =>
        {
            if (!newValue)
            {
                return;
            }
            this.contact.MailingAddresses.forEach((mailingAddress: MailingAddressViewModel) =>
            {
                mailingAddress.IsPrimary = mailingAddress.Id === newValue.Id;
                if (mailingAddress.RowState === DataRowState.Unchanged)
                {
                    mailingAddress.RowState = DataRowState.Modified;
                }
            });
        });
        setEnabled(this.phoneNumberControl, !this.isViewOnly).setValue(this.contact.PrimaryPhoneNumber);
        subscribeToValueChanges(this.phoneNumberControl, (newValue: string) =>
        {
            this.contact.PrimaryPhoneNumber = newValue;
        });
        setEnabled(this.emailControl, !this.isViewOnly).setValue(this.contact.EmailAddress);
        subscribeToValueChanges(this.emailControl, (newValue: string) =>
        {
            this.contact.EmailAddress = newValue;
        });
        setEnabled(this.phoneNumber2Control, !this.isViewOnly).setValue(this.contact.PhoneNumber2);
        subscribeToValueChanges(this.phoneNumber2Control, (newValue: string) =>
        {
            this.contact.PhoneNumber2 = newValue;
        });
        setEnabled(
            this.employerIdentificationNumberControl,
            !this.isViewOnly && this.employerIdentificationNumberData.canEdit
        );
        subscribeToValueChanges(this.employerIdentificationNumberControl, (newValue: string) =>
        {
            if (this.employerIdentificationNumberData.isMasked)
            {
                this.employerIdentificationNumberControl.setValue(this.employerIdentificationNumberData.maskedValue, {
                    emitEvent: false,
                });
                return;
            }
            if (this.employerIdentificationNumberData.canEdit)
            {
                this.employerIdentificationNumberData.unmaskedValue = newValue;
                this.employerIdentificationNumberData.maskedValue = this.getMaskedText(newValue);
                this.contact.EmployerIdentificationNumber = newValue;
            }
        });
        setEnabled(this.phoneNumber3Control, !this.isViewOnly).setValue(this.contact.PhoneNumber3);
        subscribeToValueChanges(this.phoneNumber3Control, (newValue: string) =>
        {
            this.contact.PhoneNumber3 = newValue;
        });
        setEnabled(this.socialSecurityNumberControl, !this.isViewOnly && this.socialSecurityNumberData.canEdit);
        subscribeToValueChanges(this.socialSecurityNumberControl, (newValue: string) =>
        {
            if (this.socialSecurityNumberData.isMasked)
            {
                this.socialSecurityNumberControl.setValue(this.socialSecurityNumberData.maskedValue, {
                    emitEvent: false,
                });
                return;
            }
            if (this.socialSecurityNumberData.canEdit)
            {
                this.socialSecurityNumberData.unmaskedValue = newValue;
                this.socialSecurityNumberData.maskedValue = this.getMaskedText(newValue);
                this.contact.SocialSecurityNumber = newValue;
            }
        });
        setEnabled(this.driversLicenseNumberControl, !this.isViewOnly && this.driversLicenseNumberData.canEdit);
        subscribeToValueChanges(this.driversLicenseNumberControl, (newValue: string) =>
        {
            if (this.driversLicenseNumberData.isMasked)
            {
                this.driversLicenseNumberControl.setValue(this.driversLicenseNumberData.maskedValue, {
                    emitEvent: false,
                });
                return;
            }
            if (this.driversLicenseNumberData.canEdit)
            {
                this.driversLicenseNumberData.unmaskedValue = newValue;
                this.driversLicenseNumberData.maskedValue = this.getMaskedText(newValue);
                this.contact.DriversLicenseNumber = newValue;
            }
        });
        setEnabled(this.dateOfBirthControl, !this.isViewOnly).setValue(
            typeof this.contact.DateOfBirth === "string"
                ? this.getUtcDate(this.contact.DateOfBirth)
                : this.contact.DateOfBirth
        );
        subscribeToValueChanges(this.dateOfBirthControl, (newValue: Date) =>
        {
            this.contact.DateOfBirth = newValue;
            if (this.contact.RowState !== DataRowState.Added)
            {
                this.contact.RowState = DataRowState.Modified;
            }
        });
        setEnabled(this.isNoCheckPaymentControl, !this.isViewOnly).setValue(this.contact.IsNoCheckPayment);
        subscribeToValueChanges(this.isNoCheckPaymentControl, (newValue: boolean) =>
        {
            this.contact.IsNoCheckPayment = newValue;
            if (this.contact.RowState !== DataRowState.Added)
            {
                this.contact.RowState = DataRowState.Modified;
            }
        });
        setEnabled(this.isNoCardPaymentControl, !this.isViewOnly).setValue(this.contact.IsNoCardPayment);
        subscribeToValueChanges(this.isNoCardPaymentControl, (newValue: boolean) =>
        {
            this.contact.IsNoCardPayment = newValue;
            if (this.contact.RowState !== DataRowState.Added)
            {
                this.contact.RowState = DataRowState.Modified;
            }
        });
        setEnabled(this.driversLicenseStateControl, !this.isViewOnly).setValue(this.contact.DriversLicenseState);
        subscribeToValueChanges(this.driversLicenseStateControl, (newValue: string) =>
        {
            this.contact.DriversLicenseState = newValue;
        });
        function subscribeToValueChanges<T = unknown>(
            control: AbstractControl<T>,
            subscriptionFn: (newValue: T) => void
        ): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    private setMaskedFieldData(): void
    {
        this.socialSecurityNumberData = {
            identifier: "SSN",
            canEdit: this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactEditSSN"),
            canView: this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactViewSSN"),
            unmaskedValue: "",
            maskedValue: "",
            isMasked: true,
            iconName: maskIcon.Visibility,
            tooltipMessage: "View SSN",
            control: this.socialSecurityNumberControl,
        };
        this.driversLicenseNumberData = {
            identifier: "DLN",
            canEdit: this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactEditDLN"),
            canView: this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactViewDLN"),
            unmaskedValue: "",
            maskedValue: "",
            isMasked: true,
            iconName: maskIcon.Visibility,
            tooltipMessage: "View DLN",
            control: this.driversLicenseNumberControl,
        };
        this.employerIdentificationNumberData = {
            identifier: "EIN",
            canEdit: this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactEditEIN"),
            canView: this.$securityService.hasPrivilege("TylerTech.Incode.Contact.Model.ContactViewEIN"),
            unmaskedValue: "",
            maskedValue: "",
            isMasked: true,
            iconName: maskIcon.Visibility,
            tooltipMessage: "View EIN",
            control: this.employerIdentificationNumberControl,
        };
        const self = this;
        checkEmpty(this.socialSecurityNumberData, this.contact.SocialSecurityNumber);
        checkEmpty(this.driversLicenseNumberData, this.contact.DriversLicenseNumber);
        checkEmpty(this.employerIdentificationNumberData, this.contact.EmployerIdentificationNumber);
        function checkEmpty(fieldData: MaskedFieldData, contactValue: string): void
        {
            if (!contactValue || contactValue.trim() === "")
            {
                fieldData.isMasked = false;
                fieldData.tooltipMessage = maskTooltip.Hide + fieldData.identifier;
                fieldData.iconName = maskIcon.VisibilityOff;
                fieldData.control.setValue(contactValue);
            }
            else
            {
                fieldData.isMasked = true;
                fieldData.tooltipMessage = maskTooltip.View + fieldData.identifier;
                fieldData.iconName = maskIcon.Visibility;
                fieldData.unmaskedValue = contactValue;
                fieldData.maskedValue = self.getMaskedText(contactValue);
                fieldData.control.setValue(fieldData.maskedValue);
            }
        }
    }

    onAlertsClose(value: EntityAlert[]): void
    {
        this.contact.Alerts = value;
        this.contact.Alerts.forEach(a =>
        {
            a.ParentId = this.contact.ContactId;
        });
        this.goToStep(ContactManagerSteps.Main);
    }

    attachmentCloseBehavior(): void
    {
        this.updateDocumentCount();
        this.goToStep(ContactManagerSteps.Main);
    }

    private clearNameFields(): void
    {
        this.contact.Title = "";
        this.contact.FirstName = "";
        this.contact.MiddleName = "";
        this.contact.LastName = "";
        this.contact.Suffix = "";
        // Update the UI
        this.titleControl.setValue("", { emitEvent: false });
        this.firstNameControl.setValue("", { emitEvent: false });
        this.middleNameControl.setValue("", { emitEvent: false });
        this.lastNameControl.setValue("", { emitEvent: false });
        this.suffixControl.setValue("", { emitEvent: false });
    }

    emailContact(): void
    {
        if (!this.contact.EmailAddress)
        {
            this.$commonDialogsService.showAlertMessage("Email address is empty or invalid.", "Cannot send email");
            return;
        }
        EmailShare.send({
            recipient: this.contact.EmailAddress,
            subject: "",
        });
    }

    private updateDocumentCount(): void
    {
        if (!this.contact.ContactId)
        {
            this.attachmentCount = 0;
            return;
        }
        const criteria: DocumentSearchCriteria = {
            EntityId: this.contact.ContactId,
            EntityType: this.entityType,
            DocType: undefined,
            Entity: undefined,
            OptionalCollection: undefined,
            ReturnMetadataOnly: undefined,
            KeysOnly: undefined,
            Mode: 0,
        };
        this.$documentApiService.GetDocumentCount(criteria).then((result: ExecutionResult<number>) =>
        {
            this.attachmentCount = result.Result;
        });
    }

    private goToStep(step: ContactManagerSteps): void
    {
        this.currentStep = step;
        this.setIsChildViewShown(step !== ContactManagerSteps.Main);
    }

    private setIsChildViewShown(value: boolean): void
    {
        this._childViewShown = value;
        this.viewChange.emit({ isChildView: value });
    }

    private isAddMode(): boolean
    {
        return this.contact && this.contact.RowState === DataRowState.Added;
    }

    private isContactNew(): boolean
    {
        return this.contact.RowState === DataRowState.Added;
    }

    onMailingAddressClose(value: Array<MailingAddressViewModel> | undefined): void
    {
        if (value)
        {
            this.contact.MailingAddresses = value;
            this.mailingAddressesSubject.next(value);
            this.mailingAddressControl.setValue(value.find(ma => ma.IsPrimary));
        }
        this.goToStep(ContactManagerSteps.Main);
    }

    notesCloseBehavior(value: EntityNote[]): void
    {
        this.contact.Notes = value;
        this.goToStep(ContactManagerSteps.Main);
    }

    alertsButtonClicked(): void
    {
        if (!this.contact.Alerts)
        {
            this.contact.Alerts = [];
        }
        this.goToStep(ContactManagerSteps.Alert);
    }

    private openAttachmentList(): void
    {
        this.goToStep(ContactManagerSteps.Attachment);
    }

    private openAuditLog(): Promise<void>
    {
        this.isProcessing = true;
        return this.$shellService
            .openShellFunction(
                "OpenContactAuditLog",
                this.contact.ContactId,
                false,
                "Failed to open Contact Audit Log: "
            )
            .then(_result =>
            {
                this.isProcessing = false;
            });
    }

    openMailingAddressList(): void
    {
        if (!this.contact.MailingAddresses)
        {
            this.contact.MailingAddresses = [];
        }
        this.goToStep(ContactManagerSteps.Address);
    }

    private openMiscReceiptSearch(): void
    {
        if (!this.showMiscReceiptSearchUrl)
        {
            return;
        }
        this.isProcessing = true;
        this.$shellService
            .openShellFunction(
                "OpenMiscReceiptSearch",
                this.contact.ContactId,
                false,
                "Failed to open Misc. Receipt Search."
            )
            .then(_result =>
            {
                this.isProcessing = false;
            });
    }

    private openNoteList(): void
    {
        if (!this.contact.Notes)
        {
            this.contact.Notes = [];
        }
        this.goToStep(ContactManagerSteps.Note);
    }

    private openUdfList(): void
    {
        if (!this.contact.ContactExtensions)
        {
            this.contact.ContactExtensions = [];
        }
        this.goToStep(ContactManagerSteps.Udf);
    }

    /**
     *
     * @param type
     * @param title
     * @param hasTcm
     * @param hasLaserfiche
     *
     * @deprecated what.....
     */
    private setAttachmentLabels(type: string, title: string, hasTcm: boolean, hasLaserfiche: boolean): void
    {
        this.entityType = type;
        this.attachmentTitle = title;
        this.hasTcm = hasTcm;
        this.hasLaserfiche = hasLaserfiche;
    }

    /**
     * @deprecated callers should just be updating controls values....
     */
    private updateFullName(): void
    {
        let fullName = "";
        if (this.contact.Title) fullName += this.contact.Title + " ";
        if (this.contact.FirstName) fullName += this.contact.FirstName + " ";
        if (this.contact.MiddleName) fullName += this.contact.MiddleName + " ";
        if (this.contact.LastName) fullName += this.contact.LastName + " ";
        if (this.contact.Suffix) fullName += this.contact.Suffix;
        if (fullName)
        {
            this.contact.Name = fullName.trim();
            // Update the UI
            this.nameControl.setValue(fullName.trim(), { emitEvent: false });
        }
    }

    udfCloseBehavior(value: UserDefinedData[]): void
    {
        if (value)
        {
            this.contact.ContactExtensions = value;
        }
        this.goToStep(ContactManagerSteps.Main);
    }

    /**
     * @deprecated This should just be in the nameControl valueChanges subscription... any other caller should probably be updating the control value
     */
    private updateNameDetails(): void
    {
        if (this.isCompany || !this.contact.Name)
        {
            this.clearNameFields();
            return;
        }
        this.$contactService.parseIndividualName(this.contact.Name).then(result =>
        {
            this.contact.Title = result.Title;
            this.contact.FirstName = result.FirstName;
            this.contact.MiddleName = result.MiddleName;
            this.contact.LastName = result.LastName;
            this.contact.Suffix = result.Suffix;
            // Update the UI
            this.titleControl.setValue(result.Title, { emitEvent: false });
            this.firstNameControl.setValue(result.FirstName, { emitEvent: false });
            this.middleNameControl.setValue(result.MiddleName, { emitEvent: false });
            this.lastNameControl.setValue(result.LastName, { emitEvent: false });
            this.suffixControl.setValue(result.Suffix, { emitEvent: false });
        });
    }

    getAddMailingAddressTooltip(): string
    {
        const mode = this.isAddMode() ? "Add" : this.isViewOnly ? "View" : "Edit";
        return `${mode} Mailing Address`;
    }

    getAddMailingAddressIcon(): string
    {
        return this.isAddMode() ? "add" : this.isViewOnly ? "description" : "edit";
    }

    private getUtcDate(value: string): Date
    {
        const date = new Date(value);
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    }

    // mask data until the control has focus.
    private getMaskedText(text: string): string
    {
        if (!text) return "";
        if (text.length < 5) return text;
        return text.substring(0, text.length - 4).replace(/[A-Za-z0-9]/g, "X") + text.substring(text.length - 4);
    }

    private flipMask(fieldData: MaskedFieldData): void
    {
        fieldData.isMasked = !fieldData.isMasked;
        fieldData.tooltipMessage = (fieldData.isMasked ? maskTooltip.View : maskTooltip.Hide) + fieldData.identifier;
        fieldData.iconName = fieldData.isMasked ? maskIcon.Visibility : maskIcon.VisibilityOff;
        fieldData.control.setValue(fieldData.isMasked ? fieldData.maskedValue : fieldData.unmaskedValue, {
            emitEvent: !fieldData.isMasked,
        });
    }
}
